import React from 'react';
import {
    AbstractMapView,
    AbstractMapViewProps,
    AbstractMapViewState,
    DEFAULT_FLIGHT_MAP_OPTIONS,
} from 'app/components/dashboard/network/AbstractMapView';
import TimeUtils from 'app/common/util/TimeUtils';
import { Flex } from 'app/components/common/Flex';
import { InputNumber, notification, Select } from 'antd';
import ConditionalRender from 'app/common/components/ConditionalRender';
import {
    CapacityModel,
    CountType,
    ErrorResponse,
    FlightDto,
    FlightId,
    FlightLevel_Unit,
    FlightPointDto,
    HotspotDto,
    MapTrafficVolume,
    VpdFlightDataDto,
} from 'app/model/CORE';
import { LayersIcon } from 'app/components/icons/LayersIcon';
import { FlightLevelSelector } from 'app/components/map/control/FlightLevelSelector';
import Utils from 'app/common/util/Utils';
import {
    ConvectionSource,
    ExtendedMonitoredTv,
    GetNatTracksRequest,
    MonitorAdditionalTrafficVolumeRequest,
    MonitoredMeasure,
    MonitoredRegulationOutlook,
    MonitoringDataRequest,
} from 'app/model/SOL49';
import {
    AddToCustomLayerEvent,
    AddToWorkspaceLayerEvent,
    ClearMapLayerEvent,
    DeleteCustomLayerEvent,
    HideMapLabelPopupEvent,
    LayerUpdateEvent,
    MapElementClickEvent,
    NewCustomLayerEvent,
    RemoveFromCustomLayerEvent,
    RemoveFromWorkspaceLayerEvent,
    ShowMapLabelPopupEvent,
    ShowModalEvent,
    UpdateHotspotSelection,
    UpdateMeasureSelection,
    UpdateRegulationOutlookSelection,
    UpdateTvSelection,
} from 'app/common/eventbus/Events';
import AppContext from 'app/common/context/AppContext';
import MapSelectionPopup, {
    MapSelectionValue,
    MapSelectionValues,
    NewLayerPopup,
} from 'app/components/dashboard/network/map/MapSelectionPopup';
import {
    CustomLayerUtils,
    EnvironmentLayerUtils,
    MapLayer,
    MapLayerType,
    MapLayerUtils,
    MapObjectToDisplayComparator,
    MapObjectType,
    NatLayerUtils,
    ObjectToDisplay,
    RegulatedLayerDelayLevelEnum,
    RegulationOutlookPredictionEnum,
    WeatherLayerUtils,
    WeatherMinLevel,
} from 'app/components/dashboard/network/map/Map';
import { MapLayersMenuPopup } from 'app/components/dashboard/network/map/MapLayersMenuPopup';
import { MonitoredFlow, ProvidesKey } from 'app/model/client/CORE';
import memoizeOne from 'memoize-one';
import { MemoizedLayerSelectionEntryShortcut } from 'app/components/dashboard/network/map/LayerSelectionEntryShortcut';
import { ControlDisplayOpts } from 'app/components/map/MapDisplay';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { FlightAndModelType, FlightMapOptions, ProfileSegmentHighlight } from 'app/components/map/model/MapModel';
import FlightUtils from 'app/common/util/FlightUtils';
import VerticalProfileSvg from 'app/components/map/vpd/VerticalProfileSvg';
import { AccMapDisplayPlugin } from 'app/components/map/ol/plugin/AccMapDisplayPlugin';
import { TableUtils } from 'app/common/util/TableUtils';
import MapUtils, { AlertType } from 'app/components/map/util/MapUtils';
import { MapSettings } from 'app/components/dashboard/network/map/MapSettings';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import { TfvLayerLabelContent, TfvLayerLabelTitle } from 'app/components/dashboard/network/map/TfvLayerLabel';
import { ClearOutlined } from '@ant-design/icons';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { UnorderedListOutlined } from '@ant-design/icons/lib';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { UnityApplication } from 'app/unity/UnityApplication';

import { selectAllFlows } from '../../../redux/features/dashboard/user-monitored-flows-slice';

//TODO move to fullscreen class
export interface FullscreenMapSettings {
    tfvLayerEnabled: boolean;
    measureLayerEnabled: boolean;
    weatherLayerEnabled: boolean;
    accLayerEnabled: boolean;
    militaryLabelEnabled: boolean;
    natLayerEnabled: boolean;
    hotspotLayerEnabled: boolean;
    workspaceLayerEnabled: boolean;
    //if not provided are true by default
    showLayerSelection?: boolean;
    showFlightLevelBandSelection?: boolean;
}
export const DEFAULT_FULLSCREEN_MAP_SETTINGS: FullscreenMapSettings = {
    tfvLayerEnabled: true,
    measureLayerEnabled: true,
    weatherLayerEnabled: true,
    accLayerEnabled: true,
    militaryLabelEnabled: true,
    natLayerEnabled: true,
    hotspotLayerEnabled: true,
    workspaceLayerEnabled: true,
    showLayerSelection: true,
    showFlightLevelBandSelection: true,
};

const mapStateToProps = (state: RootState) => {
    return {
        fullscreenMapSettings: state.mapSettingsData.fullscreenMapSettings,
        allFlowsSections: selectAllFlows(state),
    };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export interface FullscreenMapViewProps extends AbstractMapViewProps {
    //measures layer props
    selectedMeasures: Array<MonitoredMeasure>;
    monitoredMeasures: Array<MonitoredMeasure>;
    regulationOutlook: Array<MonitoredRegulationOutlook>;
    selectedRegulationOutlook: Array<MonitoredRegulationOutlook>;

    //hotspot layer props
    hotspots: Array<HotspotDto>;
    selectedHotspots: Array<ExtendedMonitoredTv>;

    display: CountType;

    onDisplayChange(value: CountType): void;

    //concerned by usage
    filteredTvIds: Array<string>;
}

type FullscreenMapViewPropsWithRedux = PropsFromRedux & FullscreenMapViewProps;

export interface FullscreenMapViewState extends AbstractMapViewState {
    mapTrafficVolumes?: Array<MapTrafficVolume>;
    mapMeasures?: Array<MapTrafficVolume>;
    layerIdCounter: number;

    layers: Array<MapLayer>;
    layersMenuVisible: boolean;
    layersMenuPosition: number[];

    mapLabelPopupContent?: string;
    mapLabelPopupPosition?: number[];

    selectedVpdFlightId?: FlightId; //callsign
    selectedFlightPoints?: FlightPointDto[];

    mapId?: number;
}

const MILITARY_ZONES_SEQUENCE_CHECK_INTERVAL_IN_MINUTES = 1; //when the military zones layer is active this will be the interval to check for a new updated sequence

const FULLSCREEN_DEFAULT_FLIGHT_MAP_OPTIONS = Object.assign(DEFAULT_FLIGHT_MAP_OPTIONS, { centerOnSelectedTvs: false });

export class FullscreenMapView extends AbstractMapView<FullscreenMapViewPropsWithRedux, FullscreenMapViewState> {
    private readonly MAP_CONTROL_DISPLAY_OPTIONS: ControlDisplayOpts = {
        zoomOnlyWithScroll: true,
        boxSelection: true,
        scenarioPolygonSelection: true,
    };
    private militaryZoneSequenceCheckRefreshIntervalId;

    private readonly MAP_TV_LAYER_ID = 1;
    private readonly MAP_MEASURE_LAYER_ID = 2;
    private readonly MAP_HOTSPOT_LAYER_ID = 3;
    private readonly MAP_WEATHER_LAYER_ID = 4;
    private readonly MAP_ACC_LAYER_ID = 5;
    private readonly MAP_MILITARY_LAYER_ID = 6;
    private readonly NAT_TRACKS_LAYER_ID = 7;
    private readonly WORKSPACE_LAYER_ID = MapUtils.WORKSPACE_MAP_LAYER;

    private tfvLayerLegendPopoverRef = React.createRef<ClosablePopover>();

    constructor(props) {
        super(props);

        this.state = {
            layerIdCounter: 0,
            layers: [],
            layersMenuVisible: false,
            layersMenuPosition: [],
        };
    }

    componentDidMount(): void {
        super.componentDidMount();
        this.initBusEventsListeners();
        this.initLayers();
    }

    initLayers() {
        const source = AppContext.getConvectionSource();
        let extraWeatherParams = {};
        if (source === ConvectionSource.DWD) {
            extraWeatherParams = {
                [WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS]: this.props.mapSettings?.showConvectionTvs,
            };
        } else if (source === ConvectionSource.DWD_ICONV) {
            extraWeatherParams = {
                [WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS]: this.props.mapSettings?.showConvectionTvs,
                [WeatherLayerUtils.CUSTOM_PROPERTY_HIDE_CONVECTIONS]:
                    this.props.mapSettings?.hideConvectionWithNoImpact,
            };
        }
        this.setState(() => {
            const monitoredLayer: MapLayer = {
                objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(this.props.monitoredTvs, MapObjectType.TV),
                selection: MapLayerUtils.toObjectToDisplayArray(this.props.selectedTvs, MapObjectType.TV),
                label: 'TFV',
                mapType: [MapObjectType.TV],
                priority: 4,
                showLabel: false,
                active: false,
                type: MapLayerType.TV,
                id: this.MAP_TV_LAYER_ID,
                filters: this.props.allFlowsSections.map(flow => ({
                    active: false,
                    name: flow.flowName,
                    filterKeys: flow.tvIds,
                    id: flow.uuid,
                })),
                customProperties: {
                    [MapLayerUtils.TV_LAYER_SHOW_LEGEND_PROPERTY]: false,
                    [MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]: this.props.display,
                    [MapLayerUtils.TV_LAYER_LOAD_PROPERTY]: AlertType.ORANGE,
                    [MapLayerUtils.TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY]: true,
                },
                parameters: {
                    [MapLayerUtils.TV_LAYER_FILTERED_TV_IDS_PARAMETER]: this.props.filteredTvIds,
                },
            };
            let measureLayerObjects = MapLayerUtils.toObjectToDisplayArray(
                this.props.monitoredMeasures,
                MapObjectType.MEASURE,
            );
            measureLayerObjects = measureLayerObjects.concat(
                MapLayerUtils.toObjectToDisplayArray(this.props.regulationOutlook, MapObjectType.REGULATION_OUTLOOK),
            );
            let selectedMeasureLayerObjects = MapLayerUtils.toObjectToDisplayArray(
                this.props.selectedMeasures,
                MapObjectType.MEASURE,
            );
            selectedMeasureLayerObjects = selectedMeasureLayerObjects.concat(
                MapLayerUtils.toObjectToDisplayArray(
                    this.props.selectedRegulationOutlook,
                    MapObjectType.REGULATION_OUTLOOK,
                ),
            );
            const measuresLayer: MapLayer = {
                objectsToDisplay: measureLayerObjects,
                selection: selectedMeasureLayerObjects,
                label: 'Measure',
                mapType: [MapObjectType.MEASURE],
                priority: 5,
                showLabel: false,
                active: false,
                type: MapLayerType.MEASURE,
                id: this.MAP_MEASURE_LAYER_ID,
                customProperties: {
                    [EnvironmentLayerUtils.REGULATED_SECTORS]: false,
                    [EnvironmentLayerUtils.REGULATED_LAYER_MIN_DELAY]: RegulatedLayerDelayLevelEnum.ANY,
                    [EnvironmentLayerUtils.REGULATION_OUTLOOK]: false,
                    [EnvironmentLayerUtils.REGULATION_OUTLOOK_MIN_PREDICTION]: RegulationOutlookPredictionEnum.ALL,
                },
            };
            const hotspotLayer: MapLayer = {
                objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(this.props.hotspots, MapObjectType.HOTSPOT),
                selection: MapLayerUtils.toObjectToDisplayArray(this.props.selectedHotspots, MapObjectType.HOTSPOT),
                label: 'Hotspot',
                mapType: [MapObjectType.HOTSPOT],
                priority: 5.1,
                showLabel: false,
                active: false,
                type: MapLayerType.HOTSPOT,
                id: this.MAP_HOTSPOT_LAYER_ID,
                customProperties: {},
            };
            const convectionMap = MapUtils.getConvectionMap(
                this.props.convectionData?.networkForecast,
                this.props.convectionData?.airspaceDefinition,
                this.props.timePeriodSelection ? this.props.mapRefTimePeriod : this.props.mapRefTime,
            );
            const weatherLayer: MapLayer = {
                objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(convectionMap, MapObjectType.CONVECTION),
                selection: [],
                label: 'Weather',
                mapType: [MapObjectType.CONVECTION],
                priority: 1,
                showLabel: this.props.mapSettings?.showConvectionLabel,
                active: this.props.mapSettings?.showConvection,
                type: MapLayerType.WEATHER,
                id: this.MAP_WEATHER_LAYER_ID,
                group: WeatherLayerUtils.GROUP_LAYER,
                customProperties: Object.assign(
                    {
                        [WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND]: this.props.mapSettings?.showConvectionLegend,
                        [WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL]: new WeatherMinLevel(
                            this.props.mapSettings?.convectionRiskLevel,
                        ),
                    },
                    extraWeatherParams,
                ),
                parameters: { [MapLayerUtils.LAYER_ACTIVE_CHANGE_DISABLE]: AuthorisationUtils.isFmpMapUser() }, //the fmp map dashboard has the weather layer only and so it can't be disabled
            };
            const accLayer: MapLayer = {
                objectsToDisplay: undefined,
                selection: [],
                label: 'ACCs',
                mapType: [MapObjectType.ACC],
                priority: 2,
                showLabel: false,
                active: false,
                type: MapLayerType.ENV,
                id: this.MAP_ACC_LAYER_ID,
                group: EnvironmentLayerUtils.ENVIRONMENT_LAYER,
                customProperties: { [EnvironmentLayerUtils.ACC_ECAC_ONLY]: false },
            };
            const militaryLayer: MapLayer = {
                objectsToDisplay: undefined,
                selection: [],
                label: 'Military',
                mapType: [MapObjectType.MILITARY],
                priority: 3,
                showLabel: false,
                active: false,
                type: MapLayerType.ENV,
                id: this.MAP_MILITARY_LAYER_ID,
                group: EnvironmentLayerUtils.ENVIRONMENT_LAYER,
                parameters: { [EnvironmentLayerUtils.MILITARY_ZONES_SEQUENCE_PROP]: undefined },
            };
            const natTracksLayer: MapLayer = {
                objectsToDisplay: undefined,
                selection: [],
                label: 'NAT',
                mapType: [MapObjectType.NAT_TRACK],
                priority: 5.2,
                showLabel: false,
                active: false,
                type: MapLayerType.ENV,
                id: this.NAT_TRACKS_LAYER_ID,
                parameters: {
                    [NatLayerUtils.NAT_LAYER_LAST_UPDATE_PARAMETER]: null,
                },
                customActions: {
                    Details: {
                        handler: () => {
                            this.showLayerContent(this.NAT_TRACKS_LAYER_ID);
                        },
                        icon: <UnorderedListOutlined />,
                    },
                },
            };
            const workspaceLayer: MapLayer = {
                objectsToDisplay: [],
                selection: [],
                label: 'Workspace',
                mapType: [MapObjectType.TV, MapObjectType.MEASURE],
                priority: 6, //TODO confirm
                showLabel: false,
                active: true,
                type: MapLayerType.WORKSPACE,
                id: this.WORKSPACE_LAYER_ID,
                customProperties: {
                    [MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]: this.props.display,
                    [MapLayerUtils.TV_LAYER_LOAD_PROPERTY]: AlertType.NEUTRAL,
                    [MapLayerUtils.TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY]: true,
                },
                customActions: {
                    Clear: {
                        handler: this.clearWorkspaceMapLayer,
                        icon: <ClearOutlined />,
                    },
                    Details: {
                        handler: () => this.showLayerContent(this.WORKSPACE_LAYER_ID),
                        icon: <UnorderedListOutlined />,
                    },
                },
            };
            const defaultLayers = [];
            if (this.props.fullscreenMapSettings.tfvLayerEnabled) {
                defaultLayers.push(monitoredLayer);
            }
            if (this.props.fullscreenMapSettings.measureLayerEnabled) {
                defaultLayers.push(measuresLayer);
            }
            if (this.props.fullscreenMapSettings.hotspotLayerEnabled) {
                defaultLayers.push(hotspotLayer);
            }
            if (this.props.fullscreenMapSettings.weatherLayerEnabled) {
                defaultLayers.push(weatherLayer);
            }
            if (this.props.fullscreenMapSettings.accLayerEnabled) {
                defaultLayers.push(accLayer);
            }
            if (this.props.fullscreenMapSettings.militaryLabelEnabled) {
                defaultLayers.push(militaryLayer);
            }
            if (this.props.fullscreenMapSettings.natLayerEnabled) {
                defaultLayers.push(natTracksLayer);
            }
            if (this.props.fullscreenMapSettings.workspaceLayerEnabled) {
                defaultLayers.push(workspaceLayer);
            }
            const customLayers = this.loadCustomLayersFromStorage();
            return {
                layers: [...defaultLayers, ...customLayers],
                layerIdCounter: this.getHigherId(customLayers),
            };
        });
    }

    //I had to include this method because the JSON.parse from the local storage doesn't parse the dates correctly (sets them as strings instead of Date objects) and I want to have the object from the props
    private loadCustomLayersFromStorage(): Array<MapLayer> {
        const fromMemory = CustomLayerUtils.customLayers;
        let result: Array<MapLayer>;
        if (fromMemory?.length) {
            const tvPerId = this.props.monitoredTvs.reduce(
                (
                    result: {
                        [key: string]: ExtendedMonitoredTv;
                    },
                    obj: ExtendedMonitoredTv,
                ) => {
                    result[obj.refLocation.id] = obj;
                    return result;
                },
                {},
            );
            const measurePerId = this.props.monitoredMeasures.reduce(
                (
                    result: {
                        [key: string]: MonitoredMeasure;
                    },
                    obj: MonitoredMeasure,
                ) => {
                    result[obj.measure.measureId.id] = obj;
                    return result;
                },
                {},
            );
            result = fromMemory.map(customLayer => {
                const properties = {
                    [MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]: MapLayerUtils.hasCustomProperty(
                        customLayer,
                        MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY,
                    )
                        ? customLayer.customProperties[MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]
                        : this.props.display,
                    [MapLayerUtils.TV_LAYER_LOAD_PROPERTY]: MapLayerUtils.hasCustomProperty(
                        customLayer,
                        MapLayerUtils.TV_LAYER_LOAD_PROPERTY,
                    )
                        ? customLayer.customProperties[MapLayerUtils.TV_LAYER_LOAD_PROPERTY]
                        : AlertType.NEUTRAL,
                    [MapLayerUtils.TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY]: MapLayerUtils.hasCustomProperty(
                        customLayer,
                        MapLayerUtils.TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY,
                    )
                        ? customLayer.customProperties[MapLayerUtils.TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY]
                        : true,
                };
                return Object.assign({}, customLayer, {
                    objectsToDisplay: customLayer.objectsToDisplay
                        .map(objToDisplay =>
                            objToDisplay.type === MapObjectType.TV
                                ? Object.assign(objToDisplay, { object: tvPerId[objToDisplay.object.refLocation.id] })
                                : Object.assign(objToDisplay, {
                                      object: measurePerId[objToDisplay.object.measure.measureId.id],
                                  }),
                        )
                        .filter(objToDisplay => Utils.isExists(objToDisplay.object)),
                    selection: customLayer.selection
                        ?.map(objToDisplay =>
                            objToDisplay.type === MapObjectType.TV
                                ? Object.assign(objToDisplay, { object: tvPerId[objToDisplay.object.refLocation.id] })
                                : Object.assign(objToDisplay, {
                                      object: measurePerId[objToDisplay.object.measure.measureId.id],
                                  }),
                        )
                        .filter(objToDisplay => Utils.isExists(objToDisplay.object)),
                    active: false, //TODO disabled by default?
                    customProperties: properties,
                    showLabel: false,
                    customActions: {
                        Details: {
                            handler: layer => {
                                this.showLayerContent(layer.id);
                            },
                            icon: <UnorderedListOutlined />,
                        },
                    },
                });
            });
        } else {
            result = [];
        }
        CustomLayerUtils.customLayers = result;
        return result;
    }

    private getHigherId(layers: Array<MapLayer>): number {
        if (layers?.length > 0) {
            return Math.max(...layers.map(layer => layer.id));
        }
        return 100;
    }

    private initBusEventsListeners() {
        AppContext.eventBus.register(MapElementClickEvent.NAME, (event: MapElementClickEvent) => {
            //process left click
            if (!event.rightClick && !AuthorisationUtils.isFmpMapUser()) {
                const popupValues = {};
                //check if there are elements for TFV layer
                const tfvSelectedElements = event.selection.filter(element => element.type === MapObjectType.TV);
                //check if there are elements for Measures layer
                const measuresSelectedElements = event.selection.filter(
                    element => element.type === MapObjectType.MEASURE,
                );
                //check if there are elements for Hotspot layer
                const hotspotSelectedElements = event.selection.filter(
                    element => element.type === MapObjectType.HOTSPOT,
                );
                //check if there are elements for reg outlook
                const regOutlookSelectedElements = event.selection.filter(
                    element => element.type === MapObjectType.REGULATION_OUTLOOK,
                );

                //if a single element, select/unselect it straight away
                if (event.selection.length === 1) {
                    let selectedObject, selectType;
                    if (tfvSelectedElements?.length > 0) {
                        selectedObject = this.props.selectedTvs.find(
                            etv => etv.refLocation.id === tfvSelectedElements[0].subject,
                        );
                        selectType = MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD;
                    }
                    if (measuresSelectedElements?.length > 0) {
                        selectedObject = this.props.selectedMeasures.find(
                            mm => mm.measure.measureId === measuresSelectedElements[0].subject,
                        );
                        selectType = MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD;
                    }
                    if (hotspotSelectedElements?.length > 0) {
                        selectedObject = this.props.selectedHotspots.find(
                            mtv => mtv.refLocation.id === hotspotSelectedElements[0].subject,
                        );
                        selectType = MapLayerUtils.HOTSPOT_MAP_SELECTION_POPUP_FIELD;
                    }
                    if (regOutlookSelectedElements?.length > 0) {
                        selectedObject = this.props.selectedRegulationOutlook.find(
                            mrg => mrg.regulationOutlook.aerodromeId === regOutlookSelectedElements[0].subject,
                        );
                        selectType = MapLayerUtils.REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD;
                    }
                    const triggerObj: MapSelectionValues = {};
                    const mapSelectionValue: MapSelectionValue = {
                        label: '',
                        options: [event.selection[0].subject],
                        selected: [],
                    };
                    if (!selectedObject) {
                        //select
                        mapSelectionValue.selected = [event.selection[0].subject];
                    }
                    Object.assign(triggerObj, { [selectType]: mapSelectionValue });
                    if (event.altKey) {
                        this.handleMapNewLayerPopupChange(triggerObj, event.clientXY);
                    } else {
                        this.handleMapSelectionPopupChange(triggerObj);
                    }
                    return;
                }

                if (tfvSelectedElements?.length > 0) {
                    const selectedTvs = Array.from(new Set(tfvSelectedElements.map(value => value.subject))); //avoid duplicates with the new Set;
                    Object.assign(popupValues, {
                        [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                            label: 'TFV',
                            options: selectedTvs,
                            selected: this.props.selectedTvs.map(tv => tv.refLocation.id),
                        },
                    });
                }

                if (measuresSelectedElements?.length > 0) {
                    const selectedMeasures = Array.from(new Set(measuresSelectedElements.map(value => value.subject))); //avoid duplicates with the new Set;

                    Object.assign(popupValues, {
                        [MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD]: {
                            label: 'Measures',
                            options: selectedMeasures,
                            selected: this.props.selectedMeasures.map(measure => measure.measure.measureId.id),
                        },
                    });
                }

                if (hotspotSelectedElements?.length > 0) {
                    const selectedHotspots = Array.from(new Set(hotspotSelectedElements.map(value => value.subject))); //avoid duplicates with the new Set;

                    Object.assign(popupValues, {
                        [MapLayerUtils.HOTSPOT_MAP_SELECTION_POPUP_FIELD]: {
                            label: 'Hotspots',
                            options: selectedHotspots,
                            selected: this.props.selectedHotspots.map(mtv => mtv.refLocation.id),
                        },
                    });
                }

                if (regOutlookSelectedElements?.length > 0) {
                    const selectedOutlooks = Array.from(
                        new Set(regOutlookSelectedElements.map(value => value.subject)),
                    ); //avoid duplicates with the new Set;

                    Object.assign(popupValues, {
                        [MapLayerUtils.REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD]: {
                            label: 'Regulation Outlook',
                            options: selectedOutlooks,
                            selected: this.props.selectedRegulationOutlook.map(
                                mro => mro.regulationOutlook.aerodromeId,
                            ),
                        },
                    });
                }

                if (Object.keys(popupValues)?.length) {
                    this.showMapSelectionPopup(popupValues, event.clientXY);
                }
            }
        });

        AppContext.eventBus.register(LayerUpdateEvent.NAME, (event: LayerUpdateEvent) => {
            if (this.state.mapId !== event.mapId) return; //ignore events which the target is no this map
            //TODO should be removed once we removed the old weather controls
            if (event.layer.id === this.MAP_WEATHER_LAYER_ID) {
                const currentWeatherLayer = this.state.layers.find(layer => layer.id === this.MAP_WEATHER_LAYER_ID);
                let weatherFlagsChanged = false;
                let newMapSettings: MapSettings = Object.assign({}, this.props.mapSettings);
                if (currentWeatherLayer.active !== event.layer.active) {
                    // this.handleWeatherLayerChange(event.layer.active);
                    weatherFlagsChanged = true;
                    newMapSettings = Object.assign(newMapSettings, { showConvection: event.layer.active });
                }
                if (currentWeatherLayer.showLabel !== event.layer.showLabel) {
                    // this.handleWeatherLabelVisibleChange(event.layer.showLabel);
                    weatherFlagsChanged = true;
                    newMapSettings = Object.assign(newMapSettings, { showConvectionLabel: event.layer.showLabel });
                }
                if (
                    currentWeatherLayer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND] !==
                    event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND]
                ) {
                    weatherFlagsChanged = true;
                    newMapSettings = Object.assign(newMapSettings, {
                        showConvectionLegend: event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND],
                    });
                }
                if (
                    currentWeatherLayer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL] !==
                    event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL]
                ) {
                    weatherFlagsChanged = true;
                    newMapSettings = Object.assign(newMapSettings, {
                        convectionRiskLevel: (
                            event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL] as WeatherMinLevel
                        )?.getLevel(),
                    });
                }
                if (
                    currentWeatherLayer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS] !==
                    event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS]
                ) {
                    weatherFlagsChanged = true;
                    newMapSettings = Object.assign(newMapSettings, {
                        showConvectionTvs: event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS],
                    });
                }
                if (
                    currentWeatherLayer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_HIDE_CONVECTIONS] !==
                    event.layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_HIDE_CONVECTIONS]
                ) {
                    weatherFlagsChanged = true;
                    newMapSettings = {
                        ...newMapSettings,
                        hideConvectionWithNoImpact: event.layer.customProperties[
                            WeatherLayerUtils.CUSTOM_PROPERTY_HIDE_CONVECTIONS
                        ] as boolean,
                    };
                }
                if (weatherFlagsChanged) {
                    this.props.onMapSettingsChanged(newMapSettings);
                    //do nothing else because it will be updated by receiving a new map settings
                    return;
                }
            }

            //if has count type prop and its different
            if (MapLayerUtils.hasCustomProperty(event.layer, MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY)) {
                const currentLayer = this.getLayer(event.layer.id, this.state.layers);
                if (
                    currentLayer.customProperties[MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY] !==
                    event.layer.customProperties[MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]
                ) {
                    this.props.onDisplayChange(
                        event.layer.customProperties[MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY] as CountType,
                    );
                }
            }

            //show/hide TFV layer legend based on its custom property
            if (event.layer.id === this.MAP_TV_LAYER_ID) {
                const currentLayer = this.getLayer(event.layer.id, this.state.layers);
                if (
                    currentLayer.customProperties[MapLayerUtils.TV_LAYER_SHOW_LEGEND_PROPERTY] !==
                    event.layer.customProperties[MapLayerUtils.TV_LAYER_SHOW_LEGEND_PROPERTY]
                ) {
                    event.layer.customProperties[MapLayerUtils.TV_LAYER_SHOW_LEGEND_PROPERTY]
                        ? this.tfvLayerLegendPopoverRef.current.show()
                        : this.tfvLayerLegendPopoverRef.current.hide();
                }
            }
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === event.layer.id) {
                            return event.layer;
                        }
                        return layer;
                    }),
                };
            });
        });

        AppContext.eventBus.register(AddToCustomLayerEvent.NAME, (event: AddToCustomLayerEvent) => {
            this.setState(
                prevState => {
                    return {
                        layers: prevState.layers.map(layer => {
                            if (layer.id === event.layer.id) {
                                let objectToDisplay;
                                if (event.objectType === MapObjectType.TV) {
                                    objectToDisplay = this.props.monitoredTvs.find(
                                        emtv =>
                                            MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(emtv) ===
                                            event.objectKey,
                                    );
                                } else if (event.objectType === MapObjectType.MEASURE) {
                                    objectToDisplay = this.props.monitoredMeasures.find(
                                        mm =>
                                            MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(mm) === event.objectKey,
                                    );
                                } else if (event.objectType === MapObjectType.HOTSPOT) {
                                    objectToDisplay = this.props.hotspots.find(
                                        hotspot =>
                                            MapLayerUtils.HOTSPOT_TV_ID_PROVIDER.getKey(hotspot) === event.objectKey,
                                    );
                                }
                                if (objectToDisplay) {
                                    return Object.assign({}, layer, {
                                        objectsToDisplay: [
                                            ...layer.objectsToDisplay,
                                            {
                                                type: event.objectType,
                                                object: objectToDisplay,
                                            },
                                        ],
                                    });
                                }
                                return layer;
                            } else {
                                return layer;
                            }
                        }),
                    };
                },
                () => CustomLayerUtils.updateStorage(this.state.layers),
            );
        });

        AppContext.eventBus.register(AddToWorkspaceLayerEvent.NAME, (event: AddToWorkspaceLayerEvent) => {
            if (event.objects.get(MapObjectType.TV)) {
                const tvIds = event.objects.get(MapObjectType.TV);
                if (tvIds?.length > 0) {
                    const existingTvs = this.props.monitoredTvs
                        .filter(emtv =>
                            Utils.contains(tvIds, MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(emtv)),
                        )
                        .map(emtv => MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(emtv));
                    if (existingTvs.length < tvIds.length) {
                        const missingTvs = tvIds.filter(tvId => !Utils.contains(existingTvs, tvId));
                        const request = {
                            additionalTVs: missingTvs,
                        } as MonitorAdditionalTrafficVolumeRequest;
                        // mark them as monitorable
                        ServiceFactory.sol49Service()
                            .monitorAdditionalTrafficVolume(request)
                            .then(reply => {
                                console.log(reply);
                            });
                    }

                    const monitRequest: MonitoringDataRequest = {
                        capacityModel: CapacityModel.CCM,
                        tvsWithTimePeriod: tvIds.map(tvId => ({ tvId: tvId })),
                    };

                    ServiceFactory.sol49Service()
                        .getMonitoringData(monitRequest)
                        .then(reply => {
                            if (!reply.error) {
                                if (reply.data.monitoredTvs.length < tvIds.length) {
                                    console.warn('Not able to get all tvs');
                                }
                                this.setState(prevState => {
                                    return {
                                        layers: prevState.layers.map(layer => {
                                            if (layer.id === this.WORKSPACE_LAYER_ID) {
                                                const toAdd = reply.data.monitoredTvs.map(monitTv => ({
                                                    type: MapObjectType.TV,
                                                    object: monitTv,
                                                }));
                                                return Object.assign({}, layer, {
                                                    objectsToDisplay: [...layer.objectsToDisplay, ...toAdd],
                                                });
                                            } else {
                                                return layer;
                                            }
                                        }),
                                    };
                                });
                            }
                        });
                }
            }

            if (event.objects.get(MapObjectType.MEASURE)) {
                const measureIds = event.objects.get(MapObjectType.MEASURE);
                this.setState(prevState => {
                    return {
                        layers: prevState.layers.map(layer => {
                            if (layer.id === this.WORKSPACE_LAYER_ID) {
                                const toAdd = this.props.monitoredMeasures
                                    .filter(mm =>
                                        Utils.contains(
                                            measureIds,
                                            MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(mm),
                                        ),
                                    )
                                    .map(mm => ({ type: MapObjectType.MEASURE, object: mm }));
                                return Object.assign({}, layer, {
                                    objectsToDisplay: [...layer.objectsToDisplay, ...toAdd],
                                });
                            } else {
                                return layer;
                            }
                        }),
                    };
                });
            }
        });

        AppContext.eventBus.register(RemoveFromWorkspaceLayerEvent.NAME, (event: RemoveFromWorkspaceLayerEvent) => {
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === this.WORKSPACE_LAYER_ID) {
                            const newObjectsToDisplay = layer.objectsToDisplay.filter(obj => {
                                //only keep the ones not in the event
                                return Utils.isNotExists(
                                    event.objects
                                        .get(obj.type)
                                        ?.find(key => MapLayerUtils.getObjectToDisplayKey(obj) === key),
                                );
                            });

                            return Object.assign({}, layer, { objectsToDisplay: newObjectsToDisplay });
                        }
                        return layer;
                    }),
                };
            });
        });

        AppContext.eventBus.register(RemoveFromCustomLayerEvent.NAME, (event: RemoveFromCustomLayerEvent) => {
            this.setState(
                prevState => {
                    return {
                        layers: prevState.layers.map(layer => {
                            if (layer.id === event.layer.id) {
                                const newObjectsToDisplay = layer.objectsToDisplay.filter(
                                    otd => MapLayerUtils.getObjectToDisplayKey(otd) !== event.objectKey,
                                );
                                return Object.assign({}, layer, { objectsToDisplay: newObjectsToDisplay });
                            } else {
                                return layer;
                            }
                        }),
                    };
                },
                () => CustomLayerUtils.updateStorage(this.state.layers),
            );
        });

        AppContext.eventBus.register(NewCustomLayerEvent.NAME, (event: NewCustomLayerEvent) => {
            let input: MapSelectionValues = {};
            const tvIds: Array<string> = event.objectsKeyPerType.get(MapObjectType.TV);
            if (tvIds?.length) {
                input = Object.assign(input, {
                    [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                        options: tvIds,
                        selected: tvIds,
                        label: null, //not relevant
                    },
                });
            }
            const measureIds: Array<string> = event.objectsKeyPerType.get(MapObjectType.MEASURE);
            if (measureIds?.length) {
                input = Object.assign(input, {
                    [MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD]: {
                        options: measureIds,
                        selected: measureIds,
                        label: null, //not relevant
                    },
                });
            }
            if (Object.keys(input)?.length > 0) {
                this.handleSaveNewLayer(input, event.layerName);
            }
        });

        AppContext.eventBus.register(ShowMapLabelPopupEvent.NAME, (event: ShowMapLabelPopupEvent) => {
            this.setState({
                mapLabelPopupContent: event.content,
                mapLabelPopupPosition: [event.clientX, event.clientY],
            });
        });
        AppContext.eventBus.register(HideMapLabelPopupEvent.NAME, () => {
            if (Utils.isExists(this.state.mapLabelPopupPosition)) {
                this.setState({
                    mapLabelPopupContent: null,
                    mapLabelPopupPosition: null,
                });
            }
        });
        AppContext.eventBus.register(DeleteCustomLayerEvent.NAME, (event: DeleteCustomLayerEvent) => {
            ClearMapLayerEvent.fire(this.state.mapId, event.layer);
            this.setState(
                prevState => {
                    return {
                        layers: prevState.layers.filter(layer => layer.id !== event.layer.id),
                    };
                },
                () => CustomLayerUtils.updateStorage(this.state.layers),
            );
        });
    }

    componentDidUpdate(prevProps: FullscreenMapViewProps, prevState: FullscreenMapViewState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (
            Utils.isNotEquals(
                TimeUtils.withoutTimeOfDay(prevProps?.mapRefTime),
                TimeUtils.withoutTimeOfDay(this.props?.mapRefTime),
            ) ||
            Utils.isNotEquals(
                TimeUtils.withoutTimeOfDay(prevProps?.mapRefTimePeriod?.from),
                TimeUtils.withoutTimeOfDay(this.props?.mapRefTimePeriod?.from),
            )
        ) {
            // Clear layers that need to be refreshed after day change (NAT + MILITARY)
            clearInterval(this.militaryZoneSequenceCheckRefreshIntervalId);
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        switch (layer.id) {
                            case this.MAP_MILITARY_LAYER_ID:
                            case this.NAT_TRACKS_LAYER_ID:
                                return Object.assign({}, layer, { objectsToDisplay: undefined, selection: [] });
                            default:
                                return layer;
                        }
                    }),
                };
            });
        }

        //layers objects update
        let tvsUpdate = false,
            measuresUpdate = false,
            hotspotUpdate = false;
        //tv layer
        if (
            Utils.isNotShallowEquals(prevProps?.monitoredTvs, this.props.monitoredTvs) ||
            Utils.isNotShallowEquals(prevProps?.selectedTvs, this.props.selectedTvs) ||
            Utils.isNotShallowEquals(prevProps?.filteredTvIds, this.props.filteredTvIds)
        ) {
            tvsUpdate = true;
            //update monit tv layer
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === this.MAP_TV_LAYER_ID) {
                            return Object.assign({}, layer, {
                                objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                                    this.props.monitoredTvs,
                                    MapObjectType.TV,
                                ),
                                selection: MapLayerUtils.toObjectToDisplayArray(
                                    this.props.selectedTvs,
                                    MapObjectType.TV,
                                ),
                                parameters: Object.assign({}, layer.parameters, {
                                    [MapLayerUtils.TV_LAYER_FILTERED_TV_IDS_PARAMETER]: this.props.filteredTvIds,
                                }),
                            } as MapLayer);
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }
        //measure layer
        if (
            Utils.isNotShallowEquals(prevProps?.monitoredMeasures, this.props.monitoredMeasures) ||
            Utils.isNotShallowEquals(prevProps?.selectedMeasures, this.props.selectedMeasures) ||
            Utils.isNotShallowEquals(prevProps?.regulationOutlook, this.props.regulationOutlook) ||
            Utils.isNotShallowEquals(prevProps?.selectedRegulationOutlook, this.props.selectedRegulationOutlook)
        ) {
            measuresUpdate = true;
            //update measure layer
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === this.MAP_MEASURE_LAYER_ID) {
                            let measureAndOutlooks = MapLayerUtils.toObjectToDisplayArray(
                                this.props.monitoredMeasures,
                                MapObjectType.MEASURE,
                            );
                            measureAndOutlooks = measureAndOutlooks.concat(
                                MapLayerUtils.toObjectToDisplayArray(
                                    this.props.regulationOutlook,
                                    MapObjectType.REGULATION_OUTLOOK,
                                ),
                            );
                            let selection = MapLayerUtils.toObjectToDisplayArray(
                                this.props.selectedMeasures,
                                MapObjectType.MEASURE,
                            );
                            selection = selection.concat(
                                MapLayerUtils.toObjectToDisplayArray(
                                    this.props.selectedRegulationOutlook,
                                    MapObjectType.REGULATION_OUTLOOK,
                                ),
                            );
                            return Object.assign({}, layer, {
                                objectsToDisplay: measureAndOutlooks,
                                selection: selection,
                            });
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }
        //hotspot layer
        if (
            Utils.isNotShallowEquals(prevProps?.hotspots, this.props.hotspots) ||
            Utils.isNotShallowEquals(prevProps?.selectedHotspots, this.props.selectedHotspots)
        ) {
            hotspotUpdate = true;
            //update hotspot layer
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === this.MAP_HOTSPOT_LAYER_ID) {
                            return Object.assign({}, layer, {
                                objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                                    this.props.hotspots,
                                    MapObjectType.HOTSPOT,
                                ),
                                selection: MapLayerUtils.toObjectToDisplayArray(
                                    this.props.selectedHotspots,
                                    MapObjectType.HOTSPOT,
                                ),
                            });
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }
        //custom layers
        if (this.hasCustomLayers(this.state.layers) && (tvsUpdate || measuresUpdate || hotspotUpdate)) {
            const updatedObjectsToDisplay = [
                ...MapLayerUtils.toObjectToDisplayArray(this.props.monitoredMeasures, MapObjectType.MEASURE),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.monitoredTvs, MapObjectType.TV),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.hotspots, MapObjectType.HOTSPOT),
            ];
            const updatedSelection = [
                ...MapLayerUtils.toObjectToDisplayArray(this.props.selectedMeasures, MapObjectType.MEASURE),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.selectedTvs, MapObjectType.TV),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.selectedHotspots, MapObjectType.TV),
            ];
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.type === MapLayerType.CUSTOM) {
                            return Object.assign({}, layer, {
                                objectsToDisplay: CustomLayerUtils.getObjectsToDisplayInList(
                                    updatedObjectsToDisplay,
                                    layer.objectsToDisplay,
                                ),
                                selection: CustomLayerUtils.getObjectsToDisplayInList(
                                    updatedSelection,
                                    updatedObjectsToDisplay,
                                ),
                            });
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }

        //workspace layer
        if (
            tvsUpdate ||
            measuresUpdate ||
            Utils.isNotShallowEquals(prevProps?.selectedMeasures, this.props.selectedMeasures) ||
            Utils.isNotShallowEquals(prevProps?.selectedTvs, this.props.selectedTvs) ||
            Utils.isNotShallowEquals(prevProps?.selectedHotspots, this.props.selectedHotspots)
        ) {
            const updatedObjectsToDisplay = [
                ...MapLayerUtils.toObjectToDisplayArray(this.props.monitoredMeasures, MapObjectType.MEASURE),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.monitoredTvs, MapObjectType.TV),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.hotspots, MapObjectType.HOTSPOT),
            ];
            const updatedSelection = [
                ...MapLayerUtils.toObjectToDisplayArray(this.props.selectedMeasures, MapObjectType.MEASURE),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.selectedTvs, MapObjectType.TV),
                ...MapLayerUtils.toObjectToDisplayArray(this.props.selectedHotspots, MapObjectType.TV),
            ];
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === this.WORKSPACE_LAYER_ID) {
                            const objectsToDisplayInMonitData = CustomLayerUtils.getObjectsToDisplayInList(
                                updatedObjectsToDisplay,
                                layer.objectsToDisplay,
                            );
                            const selectionInMonitData = CustomLayerUtils.getObjectsToDisplayInList(
                                updatedSelection,
                                updatedObjectsToDisplay,
                            );

                            //because workspace layer might contain objects NOT returned in the monit data, we need to keep them in the list. This should be a temporary fix till the monit data includes them
                            if (objectsToDisplayInMonitData.length !== layer.objectsToDisplay.length) {
                                const missingObjects = layer.objectsToDisplay.filter(
                                    otd =>
                                        !Utils.contains(objectsToDisplayInMonitData, otd, MapObjectToDisplayComparator),
                                );
                                if (missingObjects.length > 0) {
                                    objectsToDisplayInMonitData.push(...missingObjects);
                                }
                            }

                            return Object.assign({}, layer, {
                                objectsToDisplay: objectsToDisplayInMonitData,
                                selection: selectionInMonitData,
                            });
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }

        //weather layers
        if (
            Utils.isNotShallowEquals(prevProps?.mapSettings, this.props.mapSettings) ||
            Utils.isNotShallowEquals(prevProps?.convectionData, this.props.convectionData) ||
            // || Utils.isNotShallowEquals(prevProps?.airspaceDef, this.props.airspaceDef)//TODO use cache
            prevProps.timePeriodSelection !== this.props.timePeriodSelection ||
            Utils.isNotEquals(prevProps.mapRefTime, this.props.mapRefTime) ||
            Utils.isNotEquals(prevProps.mapRefTimePeriod, this.props.mapRefTimePeriod)
        ) {
            this.setState(prevState => {
                const source = AppContext.getConvectionSource();
                let extraParams = {};
                if (source === ConvectionSource.DWD) {
                    extraParams = {
                        [WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS]: this.props.mapSettings?.showConvectionTvs,
                    };
                } else if (source === ConvectionSource.DWD_ICONV) {
                    extraParams = {
                        [WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS]: this.props.mapSettings?.showConvectionTvs,
                        [WeatherLayerUtils.CUSTOM_PROPERTY_HIDE_CONVECTIONS]:
                            this.props.mapSettings?.hideConvectionWithNoImpact,
                    };
                }
                return {
                    layers: prevState.layers.map(layer => {
                        if (layer.id === this.MAP_WEATHER_LAYER_ID) {
                            const mapConvection = MapUtils.getConvectionMap(
                                this.props.convectionData?.networkForecast,
                                this.props.convectionData?.airspaceDefinition,
                                this.props.timePeriodSelection ? this.props.mapRefTimePeriod : this.props.mapRefTime,
                            );
                            return Object.assign({}, layer, {
                                objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                                    mapConvection,
                                    MapObjectType.CONVECTION,
                                ),
                                active: this.props.mapSettings.showConvection,
                                showLabel: this.props.mapSettings.showConvectionLabel,
                                customProperties: Object.assign(
                                    {
                                        [WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND]:
                                            this.props.mapSettings?.showConvectionLegend,
                                        [WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL]: new WeatherMinLevel(
                                            this.props.mapSettings?.convectionRiskLevel,
                                        ),
                                    },
                                    extraParams,
                                ),
                            });
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }

        //acc layer
        if (
            Utils.isNotShallowEquals(prevState?.layers, this.state.layers) &&
            Utils.isNotShallowEquals(
                this.getLayer(this.MAP_ACC_LAYER_ID, prevState?.layers),
                this.getLayer(this.MAP_ACC_LAYER_ID, this.state.layers),
            )
        ) {
            const currentAccLayer = this.getLayer(this.MAP_ACC_LAYER_ID, this.state.layers);
            if (currentAccLayer.active && Utils.isNotExists(currentAccLayer?.objectsToDisplay)) {
                //load
                ServiceFactory.sol49Service()
                    .getAccUnits()
                    .then(reply => {
                        let accToDisplay = [];
                        if (!reply.error) {
                            AccMapDisplayPlugin.ECAC_ELEMENTS = reply.data.ecacElementIds;
                            accToDisplay = MapLayerUtils.toObjectToDisplayArray(reply.data.accs, MapObjectType.ACC);

                            this.setState(prevState => {
                                return {
                                    layers: prevState.layers.map(layer => {
                                        if (layer.id === this.MAP_ACC_LAYER_ID) {
                                            return Object.assign({}, layer, { objectsToDisplay: accToDisplay });
                                        } else {
                                            return layer;
                                        }
                                    }),
                                };
                            });
                        }
                    });
            }
        }
        //nat tracks layer
        if (
            (Utils.isNotShallowEquals(prevState?.layers, this.state.layers) &&
                Utils.isNotShallowEquals(
                    this.getLayer(this.NAT_TRACKS_LAYER_ID, prevState?.layers),
                    this.getLayer(this.NAT_TRACKS_LAYER_ID, this.state.layers),
                )) ||
            Utils.isNotShallowEquals(prevProps?.mapRefTimePeriod, this.props.mapRefTimePeriod) ||
            Utils.isNotEquals(
                TimeUtils.withoutTimeOfDay(prevProps?.mapRefTime),
                TimeUtils.withoutTimeOfDay(this.props.mapRefTime),
            ) ||
            Utils.isNotEquals(
                TimePeriodUtils.withoutMinutes(prevProps?.timePeriod),
                TimePeriodUtils.withoutMinutes(this.props.timePeriod),
            ) ||
            prevProps?.timePeriodSelection !== this.props.timePeriodSelection
        ) {
            const currentLayer = this.getLayer(this.NAT_TRACKS_LAYER_ID, this.state.layers);
            if (
                currentLayer &&
                currentLayer.active &&
                (Utils.isNotExists(currentLayer?.objectsToDisplay) || //never did the request
                    //TODO how often the mapTimePeriod moves?? too many requests?
                    //changed the map time
                    Utils.isNotEquals(
                        TimeUtils.withoutTimeOfDay(prevProps?.mapRefTime),
                        TimeUtils.withoutTimeOfDay(this.props.mapRefTime),
                    ) ||
                    Utils.isNotEquals(
                        TimePeriodUtils.withoutMinutes(prevProps?.timePeriod),
                        TimePeriodUtils.withoutMinutes(this.props.timePeriod),
                    ) ||
                    prevProps?.timePeriodSelection !== this.props.timePeriodSelection ||
                    NatLayerUtils.isTimeToUpdate(currentLayer))
            ) {
                const request: GetNatTracksRequest = {
                    day: this.props.mapRefTime, //use the entire day and then the display plugin will handle the filtering
                };
                //load
                ServiceFactory.sol49Service()
                    .getNatTracks(request)
                    .then(reply => {
                        let tracksToDisplay = [];
                        if (!reply.error) {
                            tracksToDisplay = MapLayerUtils.toObjectToDisplayArray(
                                reply.data.natTracks,
                                MapObjectType.NAT_TRACK,
                            );
                            this.setState(prevState => {
                                return {
                                    layers: prevState.layers.map(layer => {
                                        if (layer.id === this.NAT_TRACKS_LAYER_ID) {
                                            return Object.assign({}, layer, {
                                                objectsToDisplay: tracksToDisplay,
                                                parameters: Object.assign({}, layer.parameters, {
                                                    [NatLayerUtils.NAT_LAYER_LAST_UPDATE_PARAMETER]: new Date(),
                                                }),
                                            });
                                        } else {
                                            return layer;
                                        }
                                    }),
                                };
                            });
                        }
                    });
            }
        }

        //military layer
        if (
            Utils.isNotShallowEquals(prevState?.layers, this.state.layers) &&
            Utils.isNotShallowEquals(
                this.getLayer(this.MAP_MILITARY_LAYER_ID, prevState?.layers),
                this.getLayer(this.MAP_MILITARY_LAYER_ID, this.state.layers),
            )
        ) {
            const currentMilitaryLayer = this.getLayer(this.MAP_MILITARY_LAYER_ID, this.state.layers);
            if (currentMilitaryLayer?.active && Utils.isNotExists(currentMilitaryLayer?.objectsToDisplay)) {
                this.retrieveDataAndUpdateMilitaryLayer()
                    .then(() => {
                        //instantiate a polling mechanism to refresh military data when required (new sequence number)
                        this.militaryZoneSequenceCheckRefreshIntervalId = setInterval(() => {
                            // Check if new data is available every 10s
                            this.checkMilitaryZoneSequenceNumber();
                        }, MILITARY_ZONES_SEQUENCE_CHECK_INTERVAL_IN_MINUTES * TimeUtils.MIN_TO_MS);
                    })
                    .catch((error: ErrorResponse) => {
                        //disable the layer and notify the user. If doesn't disable the layer, will be in loop trying to get the military zones from the server
                        this.setState(
                            prevState => {
                                return {
                                    layers: prevState.layers.map(layer => {
                                        if (layer.id === this.MAP_MILITARY_LAYER_ID) {
                                            return Object.assign({}, layer, { active: false });
                                        } else {
                                            return layer;
                                        }
                                    }),
                                };
                            },
                            () => notification.error({ message: 'Error', description: error.message, duration: 0 }),
                        );
                    });
            }
            if (!currentMilitaryLayer?.active && Utils.isExists(this.militaryZoneSequenceCheckRefreshIntervalId)) {
                clearInterval(this.militaryZoneSequenceCheckRefreshIntervalId);
            }
        }

        if (prevProps?.display !== this.props.display) {
            this.setState(prevState => {
                return {
                    layers: prevState.layers.map(layer => {
                        if (MapLayerUtils.hasCustomProperty(layer, MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY)) {
                            return Object.assign(layer, {
                                customProperties: Object.assign(layer.customProperties, {
                                    [MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]: this.props.display,
                                }),
                            });
                        } else {
                            return layer;
                        }
                    }),
                };
            });
        }

        if (Utils.isNotShallowEquals(prevProps?.flightData?.flights, this.props.flightData?.flights)) {
            const updatedSelectedVpdFlight = Utils.isExists(prevState?.selectedVpdFlightId)
                ? TableUtils.getUpdatedSingleSelection(
                      prevState.selectedVpdFlightId,
                      this.props.flightData?.flights?.map(flight => flight.flightId),
                      FullscreenMapView.FLIGHT_ID_KEY_PROVIDER,
                  )
                : undefined;
            this.setState({
                selectedVpdFlightId: Utils.isExists(updatedSelectedVpdFlight)
                    ? updatedSelectedVpdFlight
                    : this.props.flightData?.flights?.length > 0
                      ? this.props.flightData.flights.sort((f1, f2) =>
                            f2.flightId?.flightKeys?.callsign?.localeCompare(f1.flightId?.flightKeys?.callsign),
                        )[0].flightId
                      : undefined,
            });
        }
    }

    private static readonly FLIGHT_ID_KEY_PROVIDER = new ProvidesKey<FlightId>(item => item.id);

    private checkMilitaryZoneSequenceNumber() {
        const currentMilitaryLayer = this.getLayer(this.MAP_MILITARY_LAYER_ID, this.state.layers);
        if (currentMilitaryLayer.active) {
            const lastCheck: Date = new Date(
                currentMilitaryLayer.parameters[
                    EnvironmentLayerUtils.MILITARY_ZONES_SEQUENCE_LAST_CHECK_PROP
                ] as number,
            );
            const lastSequence = currentMilitaryLayer.parameters[EnvironmentLayerUtils.MILITARY_ZONES_SEQUENCE_PROP];
            //check if the last sequence check was
            if (
                Utils.isNotExists(lastCheck) ||
                TimeUtils.addMinutes(lastCheck, MILITARY_ZONES_SEQUENCE_CHECK_INTERVAL_IN_MINUTES).getTime() <=
                    new Date().getTime()
            ) {
                ServiceFactory.sol49Service()
                    .getMilitaryZonesLastSequence({ day: this.props.mapRefTime })
                    .then(reply => {
                        if (!reply.error) {
                            if (reply.data !== lastSequence) {
                                // noinspection JSIgnoredPromiseFromCall
                                this.retrieveDataAndUpdateMilitaryLayer();
                            }
                        }
                    });
            }
        }
    }

    //returning a promise so it fails when is not able to retrieve
    private retrieveDataAndUpdateMilitaryLayer(): Promise<void> {
        //load
        return new Promise<void>((resolve, reject) => {
            ServiceFactory.sol49Service()
                .getMilitaryZones({
                    datasetAndDay: { day: this.props.mapRefTime }, //TODO add dataset (to support pretact)
                    includeAirspaceDefinitions: true,
                })
                .then(reply => {
                    let zonesToDisplay = [];
                    if (!reply.error) {
                        MapLayerUtils.MILITARY_ZONES_CACHE = reply.data?.militaryZones; //update cache
                        zonesToDisplay = MapLayerUtils.toObjectToDisplayArray(
                            reply.data?.militaryZones,
                            MapObjectType.MILITARY,
                        );
                        this.setState(
                            prevState => {
                                return {
                                    layers: prevState.layers.map(layer => {
                                        if (layer.id === this.MAP_MILITARY_LAYER_ID) {
                                            return Object.assign(layer, {
                                                objectsToDisplay: zonesToDisplay,
                                                parameters: {
                                                    [EnvironmentLayerUtils.MILITARY_ZONES_SEQUENCE_PROP]:
                                                        reply.data.sequence,
                                                    [EnvironmentLayerUtils.MILITARY_ZONES_SEQUENCE_LAST_CHECK_PROP]:
                                                        reply.technicalInfo.replySendTime?.getTime(),
                                                },
                                            });
                                        } else {
                                            return layer;
                                        }
                                    }),
                                };
                            },
                            () => resolve(),
                        );
                    } else {
                        reject(reply.error);
                    }
                });
        });
    }

    private getLayer(layerId: number, list: Array<MapLayer>) {
        return list?.find(mp => mp.id === layerId);
    }

    hasCustomLayers = memoizeOne((layers: Array<MapLayer>): boolean => {
        return layers?.filter(layer => layer.type === MapLayerType.CUSTOM)?.length > 0;
    });

    showMapSelectionPopup(values: MapSelectionValues, clientXY: number[]) {
        const content = (
            <MapSelectionPopup
                values={values}
                onSelect={this.handleMapSelectionPopupChange}
                onNewLayer={this.handleMapNewLayerPopupChange}
            />
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '20em', clientXY[0], clientXY[1]));
    }

    renderVpdForFlight = (
        flight: FlightDto,
        highlightedSegments: ProfileSegmentHighlight[],
        flightMapOptions: FlightMapOptions,
    ) => {
        if (Utils.isExists(flight)) {
            const modelType = this.getHighestModelType(flight);

            //get the crossings
            const vpdCrossings: VpdFlightDataDto =
                flight && this.getVpdCrossings(FlightUtils.getEnrichedFlightId(flight));

            return (
                <VerticalProfileSvg
                    flight={flight}
                    modelType={modelType}
                    vpdCrossings={vpdCrossings}
                    showIfpuzOnly={flightMapOptions.showIfpzPartOfTrajectoryOnly}
                    highlightedPoints={null}
                    highlightedSegments={highlightedSegments}
                    pointSelectionHandler={() => {}}
                    showDynamicVolumeCrossings={flightMapOptions.showActiveDynamicVolumeInVpd}
                    airspacesOfInterest={[]}
                />
            );
        }
    };

    renderContent(): any {
        // }
        return (
            <Flex direction="horizontal" style={{ gap: '0.5em' }}>
                <ConditionalRender
                    condition={
                        Utils.isNotExists(this.props.fullscreenMapSettings.showFlightLevelBandSelection) ||
                        this.props.fullscreenMapSettings.showFlightLevelBandSelection
                    }>
                    <Flex size="5em" direction="vertical">
                        <Flex size="1vh" />
                        <Flex size="4vh">
                            <span style={{ fontWeight: 'bold', alignSelf: 'center', textAlign: 'center' }}>
                                FLIGHT LEVEL
                            </span>
                        </Flex>
                        <Flex size="4vh">
                            <AutoSubmitInputNumber
                                initialValue={this.props.flightLevelFilter.max.value}
                                onChange={value => this.changeFLMaxValue(value)}
                            />
                        </Flex>
                        <Flex size="50%">
                            <FlightLevelSelector
                                length="100%"
                                markIntervalFL={20}
                                flRange={this.props.flightLevelFilter}
                                onFlRangeChange={this.handleFlRangeChange}
                                orientation="vertical"
                                /* highlights={[{
                                     min: {value: 100, unit: FlightLevel_Unit.FLIGHT_LEVEL},
                                     max: {value: 200, unit: FlightLevel_Unit.FLIGHT_LEVEL}
                                 }]}*/
                            />
                        </Flex>
                        <Flex size="4vh" className="centered" direction="vertical">
                            <AutoSubmitInputNumber
                                initialValue={this.props.flightLevelFilter.min.value}
                                onChange={value => this.changeFLMinValue(value)}
                            />
                        </Flex>
                    </Flex>
                </ConditionalRender>
                <Flex direction="vertical">
                    <Flex className="layout-content-map-id">{this.renderMap()}</Flex>
                    {this.renderFullscreenVpd()}
                </Flex>
            </Flex>
        );
    }

    changeFLMinValue(flValue: number) {
        this.props.onFlightLevelFilterChange(
            Object.assign({}, this.props.flightLevelFilter, {
                min: { value: flValue, unit: FlightLevel_Unit.FLIGHT_LEVEL },
            }),
        );
    }
    changeFLMaxValue(flValue: number) {
        this.props.onFlightLevelFilterChange(
            Object.assign({}, this.props.flightLevelFilter, {
                max: { value: flValue, unit: FlightLevel_Unit.FLIGHT_LEVEL },
            }),
        );
    }

    renderFullscreenVpd() {
        let flight;
        if (this.props.flightData?.flights?.length === 1) {
            flight = this.props.flightData.flights[0];
        } else if (this.props.flightData?.flights?.length > 1) {
            flight = this.props.flightData.flights.find(flight =>
                FlightUtils.isSameFlight(flight.flightId, this.state.selectedVpdFlightId),
            );
        }
        if (Utils.isExists(flight)) {
            const vpdHighlightedSegments = this.vpdHighlightedSegments(this.props.highlightedSegments, flight.flightId);
            return (
                <Flex size={'25%'} style={{ backgroundColor: '#191919' }} direction="vertical">
                    <ConditionalRender condition={this.props.flightData?.flights?.length > 1}>
                        <Flex size="2em" className="centered">
                            <Select
                                style={{ width: '10em' }}
                                value={this.getFlightVpdKey(this.state.selectedVpdFlightId)}
                                listHeight={320}
                                onChange={this.handleVpdTabChange}>
                                {this.props.flightData?.flights?.map(flight => (
                                    <Select.Option
                                        key={flight.flightId.flightKeys.callsign}
                                        value={this.getFlightVpdKey(flight.flightId)}>
                                        {flight.flightId.flightKeys.callsign}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Flex>
                    </ConditionalRender>
                    <Flex>
                        {this.renderVpdForFlight(flight, vpdHighlightedSegments, FULLSCREEN_DEFAULT_FLIGHT_MAP_OPTIONS)}
                    </Flex>
                </Flex>
            );
        }
    }

    renderHeader(): any {
        return (
            <Flex size="2vw" style={{ flexWrap: 'wrap', gap: '.5em' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {this.renderHeaderTimeSlider(true)}
                </div>
                <ConditionalRender
                    condition={
                        Utils.isNotExists(this.props.fullscreenMapSettings.showLayerSelection) ||
                        this.props.fullscreenMapSettings.showLayerSelection
                    }>
                    <Flex className="detailed-subpanel-header-right">
                        <Flex style={{ gap: '1em' }}>
                            {this.state.layers
                                .filter(layer => layer.type !== MapLayerType.CUSTOM)
                                .map(layer => {
                                    return (
                                        <MemoizedLayerSelectionEntryShortcut
                                            key={layer.id}
                                            mapId={this.state.mapId}
                                            layer={layer}
                                        />
                                    );
                                })}
                        </Flex>
                        <Flex className="centered" size="2em">
                            {this.renderMapLayersMenuPopup()}
                        </Flex>
                    </Flex>
                </ConditionalRender>
            </Flex>
        );
    }

    renderMapLayersMenuPopup() {
        //this needs to be always in the DOM because it has the components responsible for displaying the layers objects into the map
        return (
            <div style={{ display: this.state.layers?.length > 1 ? 'flex' : 'none' }}>
                <LayersIcon className="icon" title={'LAYERS'} onClick={this.handleMapLayersMenuVisibleChange} />
                <MapLayersMenuPopup
                    layers={this.state.layers}
                    timePeriod={
                        this.props.timePeriodSelection
                            ? this.props.mapRefTimePeriod
                            : {
                                  from: this.props.mapRefTime,
                                  to: this.props.mapRefTime,
                              }
                    }
                    flightLevelBand={this.props.flightLevelFilter}
                    visible={this.state.layersMenuVisible}
                    // left={'calc(100% - 60em)'} //TODO find better way of moving the menu to the left to avoid being outside the screen
                    //using old way as now it seems the measures and flights section are displayed in the (not so) fullscreen amp
                    left={this.state.layersMenuPosition[0]}
                    top={this.state.layersMenuPosition[1]}
                    onClose={() =>
                        this.setState({
                            layersMenuVisible: false,
                        })
                    }
                    mapId={this.state.mapId}
                />
            </div>
        );
    }

    renderMap() {
        const customControls = (
            <div style={{ zIndex: 1, margin: '5px' }}>
                {this.renderConvectionLegend()}
                {this.renderTfvLayerLegend()}
                {this.renderMapLabelPopup()}
            </div>
        );

        // NOTE(bjarne): In mapless mode the UnityApplication is already created in App.tsx.
        return this.nmui4dContext?.isMapless ? (
            customControls
        ) : (
            <UnityApplication customControls={customControls}></UnityApplication>
        );
    }

    renderTfvLayerLegend = () => {
        return (
            <span style={{ width: '100%' }} className="ol-full-screen">
                <ClosablePopover
                    key="tfv-layer-label"
                    ref={this.tfvLayerLegendPopoverRef}
                    trigger="click"
                    placement="bottomLeft"
                    title={
                        <TfvLayerLabelTitle
                            onClose={() => {
                                this.handleTfvLayerLegendClose();
                            }}
                        />
                    }
                    autoHide={false}
                    content={<TfvLayerLabelContent />}></ClosablePopover>
            </span>
        );
    };

    renderMapLabelPopup() {
        return (
            <ConditionalRender condition={Utils.isExists(this.state.mapLabelPopupPosition)}>
                <div
                    style={{
                        color: 'rgb(0, 141, 186)',
                        padding: '10px',
                        backgroundColor: '#3A3A3A',
                        borderRadius: '25px',
                        // zIndex: 10000,
                        position: 'absolute',
                        left: this.state.mapLabelPopupPosition && this.state.mapLabelPopupPosition[0],
                        top: this.state.mapLabelPopupPosition && this.state.mapLabelPopupPosition[1],
                    }}>
                    {this.state.mapLabelPopupContent}
                </div>
            </ConditionalRender>
        );
    }

    // HANDLERS
    handleMapSelectionPopupChange = (value: MapSelectionValues) => {
        //TODO replace direct access to ids by ProvidesKey
        if (value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]) {
            const selectedTvs: Array<string> = Array.isArray(value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected)
                ? value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected
                : [];
            const notSelectedTvs: Array<string> = value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].options.filter(
                tv => !selectedTvs.includes(tv),
            );

            const newSelection = this.props.selectedTvs.filter(stv => !notSelectedTvs.includes(stv.refLocation.id));
            for (const selectedTv of selectedTvs) {
                const itemFromMonitoredTvs = this.props.monitoredTvs.find(mtv => mtv.refLocation.id === selectedTv);
                if (itemFromMonitoredTvs)
                    //sanity check
                    newSelection.push(itemFromMonitoredTvs);
                //TODO remove once dashboard data has additional tvs on the reply even if they're not active
                /* removed because it doesn't work quite well
                const workspaceLayer = this.state.layers.find(layer => layer.id === this.WORKSPACE_LAYER_ID);
                if(workspaceLayer){
                    const itemFromWorkspaceLayer = workspaceLayer.objectsToDisplay.find(otd => otd.type === MapObjectType.TV && MapLayerUtils.getObjectToDisplayKey(otd) === selectedTv);
                    if(itemFromWorkspaceLayer?.object) {
                        newSelection.push(itemFromWorkspaceLayer.object)
                    }
                }*/
            }
            AppContext.eventBus.publish(new UpdateTvSelection('replace', newSelection));
        }
        if (value[MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD]) {
            const selectedMeasures: Array<string> = Array.isArray(
                value[MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD].selected,
            )
                ? value[MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD].selected
                : [];
            const notSelectedMeasures: Array<string> = value[
                MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD
            ].options.filter(tv => !selectedMeasures.includes(tv));

            const newSelection = this.props.selectedMeasures.filter(
                sm => !notSelectedMeasures.includes(sm.measure.measureId.id),
            );
            for (const selectedMeasure of selectedMeasures) {
                newSelection.push(this.props.monitoredMeasures.find(mm => mm.measure.measureId.id === selectedMeasure));
            }
            AppContext.eventBus.publish(new UpdateMeasureSelection('replace', newSelection));
        }
        if (value[MapLayerUtils.HOTSPOT_MAP_SELECTION_POPUP_FIELD]) {
            const selectedHotspots: Array<string> = Array.isArray(
                value[MapLayerUtils.HOTSPOT_MAP_SELECTION_POPUP_FIELD].selected,
            )
                ? value[MapLayerUtils.HOTSPOT_MAP_SELECTION_POPUP_FIELD].selected
                : [];
            const notSelectedHotspots: Array<string> = value[
                MapLayerUtils.HOTSPOT_MAP_SELECTION_POPUP_FIELD
            ].options.filter(tv => !selectedHotspots.includes(tv));
            const newSelection = this.props.selectedHotspots.filter(
                mtv => !notSelectedHotspots.includes(mtv.refLocation.id),
            );
            for (const selectedHotspot of selectedHotspots) {
                newSelection.push(this.props.monitoredTvs.find(mtv => mtv.refLocation.id === selectedHotspot));
            }
            AppContext.eventBus.publish(new UpdateHotspotSelection('replace', newSelection));
        }
        if (value[MapLayerUtils.REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD]) {
            const selectedRegOutlook: Array<string> = Array.isArray(
                value[MapLayerUtils.REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD].selected,
            )
                ? value[MapLayerUtils.REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD].selected
                : [];
            const notSelected: Array<string> = value[
                MapLayerUtils.REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD
            ].options.filter(ad => !selectedRegOutlook.includes(ad));
            const newSelection = this.props.selectedRegulationOutlook.filter(
                mro => !notSelected.includes(mro.regulationOutlook.aerodromeId),
            );
            for (const selectedOutlook of selectedRegOutlook) {
                newSelection.push(
                    this.props.regulationOutlook.find(mro => mro.regulationOutlook.aerodromeId === selectedOutlook),
                );
            }
            AppContext.eventBus.publish(new UpdateRegulationOutlookSelection('replace', newSelection));
        }
    };

    handleMapNewLayerPopupChange = (value: MapSelectionValues, position: number[]) => {
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, content, '200px', position[0] - 100, position[1] - 200),
        );
    };

    handleSaveNewLayer(value: MapSelectionValues, name: string) {
        //TODO replace direct access to ids by ProvidesKey
        const tvIds = value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]?.selected;
        const measureIds = value[MapLayerUtils.MEASURE_MAP_SELECTION_POPUP_FIELD]?.selected;
        const objectsToDisplay: Array<ObjectToDisplay> = [];
        const selection: Array<ObjectToDisplay> = [];
        if (tvIds?.length) {
            objectsToDisplay.push(
                ...this.props.monitoredTvs
                    .filter(mtv => tvIds.includes(mtv.refLocation.id))
                    .map(mtv => ({
                        type: MapObjectType.TV,
                        object: mtv,
                    })),
            );
            selection.push(
                ...this.props.selectedTvs
                    ?.filter(selectedTv => tvIds.includes(selectedTv.refLocation.id))
                    .map(mtv => ({
                        type: MapObjectType.TV,
                        object: mtv,
                    })),
            );
        }
        if (measureIds?.length) {
            objectsToDisplay.push(
                ...this.props.monitoredMeasures
                    .filter(mm => measureIds.includes(mm.measure.measureId.id))
                    .map(mm => ({
                        type: MapObjectType.MEASURE,
                        object: mm,
                    })),
            );
            selection.push(
                ...this.props.selectedMeasures
                    ?.filter(selectedMeasure => measureIds.includes(selectedMeasure.measure.measureId.id))
                    .map(mm => ({
                        type: MapObjectType.MEASURE,
                        object: mm,
                    })),
            );
        }

        this.setState(
            prevState => {
                const newLayer: MapLayer = {
                    id: prevState.layerIdCounter + 1,
                    showLabel: false,
                    active: false,
                    filters: [],
                    type: MapLayerType.CUSTOM,
                    mapType: [MapObjectType.MEASURE, MapObjectType.TV],
                    priority: 10,
                    group: CustomLayerUtils.GROUP_LAYER,
                    label: name,
                    objectsToDisplay: objectsToDisplay,
                    selection: selection,
                    customProperties: {
                        [MapLayerUtils.TV_LAYER_COUNT_TYPE_PROPERTY]: this.props.display,
                        [MapLayerUtils.TV_LAYER_LOAD_PROPERTY]: AlertType.NEUTRAL,
                        [MapLayerUtils.TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY]: true,
                    },
                    customActions: {
                        Details: {
                            handler: layer => {
                                this.showLayerContent(layer.id);
                            },
                            icon: <UnorderedListOutlined />,
                        },
                    },
                };
                return {
                    layers: prevState.layers.concat(newLayer),
                    layerIdCounter: newLayer.id,
                };
            },
            () => {
                CustomLayerUtils.updateStorage(this.state.layers);
            },
        );
    }

    handleMapLayersMenuVisibleChange = event => {
        this.setState(prevState => ({
            layersMenuVisible: !prevState.layersMenuVisible,
            layersMenuPosition: [event.clientX, event.clientY],
        }));
    };

    handleVpdTabChange = (activeTab: string) => {
        const flightId = this.props.flightData.flights.find(
            flight => this.getFlightVpdKey(flight.flightId) === activeTab,
        )?.flightId;
        this.setState({
            selectedVpdFlightId: flightId,
        });
    };

    handleMapFlightSelection = (flightAndModel: FlightAndModelType) => {
        this.setState({
            selectedVpdFlightId: flightAndModel.flight.flightId,
        });
    };

    handleMapFlightPointSelection = (flightAndModel: FlightAndModelType, flightPoint: FlightPointDto) => {
        this.setState(prevState => {
            if (FlightUtils.isSameFlight(prevState.selectedVpdFlightId, flightAndModel.flight.flightId)) {
                return {
                    selectedFlightPoints: [flightPoint],
                };
            } else {
                return {
                    selectedVpdFlightId: flightAndModel.flight.flightId,
                    selectedFlightPoints: [flightPoint],
                };
            }
        });
    };

    handleTfvLayerLegendClose = () => {
        const tfvLayer = this.state.layers.find(layer => layer.id === this.MAP_TV_LAYER_ID);
        if (tfvLayer) {
            const newCustomProperties = Object.assign({}, tfvLayer.customProperties, {
                [MapLayerUtils.TV_LAYER_SHOW_LEGEND_PROPERTY]: false,
            });
            const newValue = Object.assign({}, tfvLayer, { customProperties: newCustomProperties });
            LayerUpdateEvent.fire(this.state.mapId, newValue);
        }
    };

    private editTvLayerFilterFromFlow(flow: MonitoredFlow) {
        this.setState(prevState => {
            return {
                layers: prevState.layers.map(layer => {
                    if (layer.id === this.MAP_TV_LAYER_ID) {
                        const newFilters = layer.filters.map(filter => {
                            if (filter.id === flow.uuid) {
                                return Object.assign(filter, { name: flow.flowName, filterKeys: flow.tvIds });
                            } else {
                                return filter;
                            }
                        });
                        return Object.assign(layer, { filters: newFilters });
                    } else {
                        return layer;
                    }
                }),
            } as FullscreenMapViewState;
        });
    }

    private getSelectedVpdFlight = memoizeOne((flightId: FlightId, flights: Array<FlightDto>) => {
        if (flights?.length > 0 && Utils.isExists(flightId)) {
            const flight: FlightDto = flights.find(predicate => FlightUtils.isSameFlight(predicate.flightId, flightId));
            if (flight) {
                const modelType = this.getHighestModelType(flight);
                if (flight && modelType) {
                    return new FlightAndModelType(flight, modelType);
                }
            }
        }
        return undefined;
    });

    private clearWorkspaceMapLayer() {
        this.setState(prevState => {
            return {
                layers: prevState.layers.map(layer => {
                    if (layer.id === this.WORKSPACE_LAYER_ID) {
                        return Object.assign({}, layer, { objectsToDisplay: [] });
                    }
                    return layer;
                }),
            };
        });
    }
    private showLayerContent(id: number) {
        const mapLayer = this.state.layers.find(layer => layer.id === id);
        if (mapLayer) {
            ModalHandler.showMapLayerContent(mapLayer);
        }
    }

    private getFlightVpdKey(flightId: FlightId): string {
        if (Utils.isNotExists(flightId)) return null;

        if (Utils.isExists(flightId?.id)) {
            return flightId.id;
        }
        //because pretact flights don't have ipflId
        return `${flightId.flightKeys.callsign}-${TimeUtils.formatDDHHMM(flightId.flightKeys.eobt)}`;
    }
}

interface AutoSubmitInputNumberProps {
    onChange(value: number): void;
    initialValue: number;
}

interface AutoSubmitInputNumberState {
    value: number;
}
class AutoSubmitInputNumber extends React.PureComponent<AutoSubmitInputNumberProps, AutoSubmitInputNumberState> {
    constructor(props) {
        super(props);
        this.state = {
            value: props.initialValue,
        };
    }

    componentDidUpdate(
        prevProps: Readonly<AutoSubmitInputNumberProps>,
        prevState: Readonly<AutoSubmitInputNumberState>,
        snapshot?: any,
    ) {
        if (prevProps.initialValue !== this.props.initialValue) {
            this.setState({
                value: this.props.initialValue,
            });
        }
    }

    render() {
        return (
            <InputNumber
                style={{ height: '1.7em', width: '4.5em' }}
                value={this.state.value}
                step={10}
                min={0}
                max={999}
                onChange={this.handleOnChange}
                onKeyDown={this.handleKeyDown}
                onStep={this.handleOnStep}
                onBlur={this.handleLostFocus}
                onFocus={this.handleOnFocus}
            />
        );
    }

    handleOnFocus = event => {
        event.target.select();
    };
    handleOnChange = value => {
        if (!isNaN(Number(value))) {
            this.setState({ value: value });
        }
    };

    handleOnStep = value => {
        this.props.onChange(value);
    };

    handleKeyDown = ev => {
        if (ev.keyCode === 13 && !ev.shiftKey) {
            this.props.onChange(this.state.value);
            this.setState({
                value: undefined,
            });
            ev.stopPropagation();
            ev.preventDefault();
        }
    };

    handleLostFocus = () => {
        //reset
        this.setState({ value: this.props.initialValue });
    };
}

export default connector(FullscreenMapView);
